
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Search,
    MoreHorizontal,
    Mail,
    Phone,
    Ban,
    CheckCircle,
    ArrowLeft,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { toast } from '@/hooks/use-toast';
import { getAllUsers, updateUserStatus } from '@/services/user';
import { PaginationControl } from '@/components/shared/PaginationControl';
import { pagination } from '@/constants';
import { capitalizeWords } from '@/helper';

interface Broker {
    _id: string;
    name: string;
    email: string;
    phone: string;
    company?: string;
    licenseNumber?: string;
    accountStatus: boolean;
    propertyCount: number;
    createdAt: string;
}

const Brokers = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [brokers, setBrokers] = useState<Broker[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);

    // Debounce search query
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchQuery]);

    const fetchBrokers = async (page = pagination.page) => {
        setIsLoading(true);
        try {
            const { data, pagination: paginationData } = await getAllUsers({
                userType: 'BROKER',
                page: page,
                limit: pagination.limit,
                searchQuery: debouncedSearchQuery,
                status: statusFilter !== 'all' ? (statusFilter === 'active') : undefined
            });

            if (Array.isArray(data)) {
                setBrokers(data);
                if (paginationData) {
                    setTotalPages(paginationData.totalPages);
                }
            } else {
                console.error("Unexpected API response format:", data);
                setBrokers([]);
            }
        } catch (error) {
            console.error('Error fetching brokers:', error);
            toast({
                title: "Error",
                description: "Failed to load brokers",
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchBrokers();
    }, [debouncedSearchQuery, statusFilter]);



    const handleStatusChange = async (id: string, newStatus: boolean) => {
        try {
            await updateUserStatus(id, newStatus);
            toast({
                title: "Status Updated",
                description: `Broker account ${newStatus ? 'activated' : 'deactivated'} successfully`,
            });
            // Refresh brokers after status change
            fetchBrokers();
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to update broker status",
                variant: "destructive"
            });
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Header />

            <div className="pt-32 pb-20 bg-secondary/30 flex-1">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-bold">Brokers Management</h1>
                            <p className="text-muted-foreground">Manage broker accounts and performance</p>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" asChild>
                                <Link to="/admin/dashboard">
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Back to Dashboard
                                </Link>
                            </Button>
                        </div>
                    </div>

                    <Card className="mb-8">
                        <CardHeader className="pb-3">
                            <CardTitle>Filters</CardTitle>
                            <CardDescription>Search and filter brokers</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="text"
                                        placeholder="Search by name, email, or company..."
                                        className="pl-8"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-full md:w-[200px]">
                                        <SelectValue placeholder="Filter by status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All</SelectItem>
                                        <SelectItem value="active">Active</SelectItem>
                                        <SelectItem value="inactive">Inactive</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                        <CardFooter>
                            {/* <div className="text-sm text-muted-foreground">
                                Showing {brokers.length} brokers
                            </div> */}
                        </CardFooter>
                    </Card>

                    {isLoading ? (
                        <div className="rounded-md border bg-card">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Broker</TableHead>
                                        <TableHead>Contact Info</TableHead>
                                        <TableHead>Company</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Listings</TableHead>
                                        <TableHead>Joined</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <TableRow key={i}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <Skeleton className="h-10 w-10 rounded-full" />
                                                    <div className="space-y-1">
                                                        <Skeleton className="h-4 w-32" />
                                                        <Skeleton className="h-3 w-20" />
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col gap-1">
                                                    <Skeleton className="h-3 w-40" />
                                                    <Skeleton className="h-3 w-32" />
                                                </div>
                                            </TableCell>
                                            <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                                            <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                            <TableCell className="text-right"><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <div className="rounded-md border bg-card">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Broker</TableHead>
                                        <TableHead>Contact Info</TableHead>
                                        <TableHead>Company</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Listings</TableHead>
                                        <TableHead>Joined</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {brokers.length > 0 ? (
                                        brokers.map((broker) => (
                                            <TableRow key={broker._id}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-10 w-10 rounded-full overflow-hidden bg-secondary flex items-center justify-center">
                                                            <span className="text-lg font-semibold">
                                                                {broker.name.charAt(0).toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <div className="font-medium">{capitalizeWords(broker.name)}</div>
                                                            {broker.licenseNumber && (
                                                                <div className="text-xs text-muted-foreground">
                                                                    License: {broker.licenseNumber}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center text-sm">
                                                            <Mail className="h-3 w-3 mr-2 text-muted-foreground" />
                                                            {broker.email}
                                                        </div>
                                                        <div className="flex items-center text-sm">
                                                            <Phone className="h-3 w-3 mr-2 text-muted-foreground" />
                                                            {broker.phone}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span>{capitalizeWords(broker.company || 'N/A')}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={broker.accountStatus ? 'bg-green-100 text-green-800 hover:bg-green-100' : 'bg-red-100 text-red-800 hover:bg-red-100'}>
                                                        {broker.accountStatus ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>{broker.propertyCount || 0}</TableCell>
                                                <TableCell>{formatDate(broker.createdAt)}</TableCell>
                                                <TableCell className="text-right">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="sm">
                                                                <MoreHorizontal className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                                            <DropdownMenuItem asChild>
                                                                <Link to={`/admin/broker-profile/${broker._id}`}>
                                                                    View Profile
                                                                </Link>
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem ><Link to={`/admin/property-management?broker=${broker._id}`}>
                                                                {/* <Home className="h-4 w-4 mr-2" /> */}
                                                                View Listings
                                                            </Link></DropdownMenuItem>
                                                            <DropdownMenuSeparator />
                                                            {!broker.accountStatus && (
                                                                <DropdownMenuItem onClick={() => handleStatusChange(broker._id, true)}>
                                                                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                                                    Activate Account
                                                                </DropdownMenuItem>
                                                            )}
                                                            {broker.accountStatus && (
                                                                <DropdownMenuItem onClick={() => handleStatusChange(broker._id, false)}>
                                                                    <Ban className="h-4 w-4 mr-2 text-red-600" />
                                                                    Deactivate Account
                                                                </DropdownMenuItem>
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                                No brokers found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </div>

            {/* Pagination */}
            <div className="flex justify-center my-4">
                <PaginationControl
                    key={`${debouncedSearchQuery}-${statusFilter}`} // Reset on filter change
                    totalPages={totalPages}
                    onPageChange={fetchBrokers}
                />
            </div>

            <Footer />
        </div>
    );
};

export default Brokers;
